'use strict';

/**
 * Paging arithmetic — "which options belong on the current page?"
 *
 * A long question lays its options out across several pages (`displaymax`).
 * Deciding which indices sit on the current page, and how many rows that page
 * shows, is pure arithmetic on the question definition — no answer data, so
 * none of the trust considerations that gate the other domain rules apply here.
 *
 * These functions are the data-only core of three legacy widget helpers, which
 * now delegate to them and keep doing the DOM work themselves:
 *
 *   - `helpers.js` `getDefaultPhaseRange` → `phaseRange`
 *   - `helpers.js` `handlePagedVisibility` → `pagedVisibility` (then the widget
 *     shows/hides each returned index)
 *   - `Base.visible_rows_count` / `grid` `getVisibleRowsCount` → `pageRowCount`
 *     (the widget measures the counts off the DOM and passes them in)
 *
 * See `./README.md` for the domain-layer contract.
 */

/**
 * The half-open-free index window a page covers. The data-only port of
 * `getDefaultPhaseRange`: page `phase_pos` starts at `phase_pos * displaymax`
 * and spans `displaymax` indices, so its last index is `min + displaymax - 1`.
 *
 * The arithmetic is kept byte-for-byte, including its behavior when a field is
 * absent — `undefined * displaymax` is `NaN`, and `pagedVisibility` reads a
 * `NaN` range as "show everything", exactly as the legacy predicate did.
 *
 * @param {Object} params `{ phase_pos, displaymax }` from the question object.
 * @returns {{min: number, max: number}} Inclusive index range for the page.
 */
export function phaseRange(params) {
  params = params || {};
  var min = params.phase_pos * params.displaymax;
  return { min: min, max: min + params.displaymax - 1 };
}

/**
 * Split `itemCount` indices into the ones a page shows and the ones it hides,
 * given that page's `range`. The data-only port of `handlePagedVisibility`: an
 * index outside `[range.min, range.max]` is hidden, everything else is shown.
 * Operates on indices only — the caller maps them back to elements and applies
 * the show/hide. A `NaN` range (an absent `displaymax`) shows every index,
 * matching the legacy `idx < min || idx > max` test.
 *
 * @param {number} itemCount How many indices the page draws from.
 * @param {{min: number, max: number}} range The page's index window.
 * @returns {{show: number[], hide: number[]}} Indices to show and to hide.
 */
export function pagedVisibility(itemCount, range) {
  var show = [];
  var hide = [];
  for (var idx = 0; idx < itemCount; idx++) {
    if (idx < range.min || idx > range.max) {
      hide.push(idx);
    } else {
      show.push(idx);
    }
  }
  return { show: show, hide: hide };
}

/**
 * How many rows the current page shows. The data-only port of the arithmetic in
 * `Base.visible_rows_count` and `grid` `getVisibleRowsCount`: normally the row
 * count as measured, but a transposed grid pages over cells, so its visible-row
 * count is the cell count divided by the number of columns. The caller measures
 * both figures off the DOM (the row/cell count and, when transposed, the column
 * count) and passes them in; a falsy `divisor` means "not transposed", so the
 * count is returned unchanged rather than divided by zero.
 *
 * @param {number} phaseRowCount Rows (or, when transposed, cells) on the page.
 * @param {number} [divisor] Column count, for a transposed grid only.
 * @returns {number} Visible rows on the current page.
 */
export function pageRowCount(phaseRowCount, divisor) {
  return divisor ? phaseRowCount / divisor : phaseRowCount;
}
